import React from 'react';
import { Clock, ChevronRight, Link2, Plus, Check, Zap } from 'lucide-react';
import { VerdictPill } from './VerdictPill';
import { EffortTagPill, type EffortTag } from './EffortTagPill';
import type { VerdictKey } from '../theme/decisionTokens';

export interface RowSurfacedReason {
  contextLabel: string;
  contextValue: string;
  relevance: string;
}

export interface ExplorerRowBrief {
  id: string;
  rank: number;
  title: string;
  category: string;
  verdict: VerdictKey;
  roiShort: string;
  ttv: string;
  effortTag: EffortTag;
}

export interface ExplorerRowProps {
  brief: ExplorerRowBrief;
  onOpen: () => void;
  onPrefetch?: () => void;
  highlight?: boolean;
  compareChecked?: boolean;
  onToggleCompare?: () => void;
  compareDisabled?: boolean;
  surfacedReasons?: RowSurfacedReason[];
  onTogglePortfolio?: () => void;
  isInPortfolio?: boolean;
  portfolioLocked?: boolean;
}

export const ExplorerRow: React.FC<ExplorerRowProps> = ({
  brief,
  onOpen,
  onPrefetch,
  highlight = false,
  compareChecked,
  onToggleCompare,
  compareDisabled = false,
  surfacedReasons,
  onTogglePortfolio,
  isInPortfolio = false,
  portfolioLocked = false,
}) => {
  const topReason = surfacedReasons && surfacedReasons.length > 0 ? surfacedReasons[0] : null;
  const reasonTitle = surfacedReasons?.map((r) => `• ${r.relevance} (${r.contextLabel}: ${r.contextValue})`).join('\n');
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onMouseEnter={onPrefetch}
      onFocus={onPrefetch}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={`flex items-center gap-4 px-5 py-3.5 border-b border-[#e5e7eb] cursor-pointer transition-colors group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6E4C]/50 ${
        highlight ? 'bg-[#FFF1ED]' : 'bg-white hover:bg-[#F5F7FA]'
      }`}
    >
      <div className="w-9 h-9 rounded-full bg-[#18365E] text-white font-bold text-sm flex items-center justify-center shrink-0">
        {brief.rank}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-bold text-[#18365E] group-hover:text-[#FF6E4C] transition-colors">{brief.title}</h3>
          <VerdictPill verdict={brief.verdict} />
        </div>
        <span className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">{brief.category}</span>
        {topReason && (
          <p className="mt-0.5 flex items-center gap-1 text-[10px] text-[#717182] min-w-0" title={reasonTitle}>
            <Link2 className="w-3 h-3 shrink-0 text-[#18365E]/60" />
            <span className="truncate">
              Why surfaced: {topReason.relevance}
              {surfacedReasons && surfacedReasons.length > 1 && (
                <span className="font-semibold text-[#18365E]/70"> +{surfacedReasons.length - 1} more</span>
              )}
            </span>
          </p>
        )}
      </div>

      <div className="text-right shrink-0 hidden sm:block w-28">
        <span className="text-[10px] text-[#717182] block">Est. value</span>
        <span className="text-sm font-bold text-[#18365E]">
          {brief.roiShort}
          <span className="text-[10px] font-medium text-[#717182]">/yr</span>
        </span>
      </div>

      <div className="shrink-0 hidden md:flex items-center gap-1.5 text-xs text-[#717182] w-24">
        <Clock className="w-3.5 h-3.5" />
        {brief.ttv}
      </div>

      <div className="shrink-0 hidden lg:block w-20">
        <EffortTagPill tag={brief.effortTag} />
      </div>

      <span className="shrink-0 flex w-56 items-center justify-end gap-3 whitespace-nowrap">
        {onTogglePortfolio && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onTogglePortfolio(); }}
            title={portfolioLocked ? 'Upgrade to add to portfolio' : isInPortfolio ? 'Remove from Portfolio' : 'Add to Portfolio'}
            aria-label={isInPortfolio ? `Remove ${brief.title} from portfolio` : `Add ${brief.title} to portfolio`}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all shrink-0 ${
              portfolioLocked
                ? 'bg-[#e5e7eb] text-[#717182] hover:bg-[#d1d5db]'
                : isInPortfolio
                  ? 'bg-[#FF6E4C] text-white shadow-sm hover:bg-[#FF6E4C]/90'
                  : 'bg-[#F5F7FA] text-[#18365E] border border-[#e5e7eb] hover:bg-[#FF6E4C]/10 hover:border-[#FF6E4C]/30'
            }`}
          >
            {portfolioLocked ? (
              <><Zap className="w-3 h-3" />Upgrade</>
            ) : isInPortfolio ? (
              <><Check className="w-3 h-3" />Added</>
            ) : (
              <><Plus className="w-3 h-3" />Portfolio</>
            )}
          </button>
        )}
        {onToggleCompare && (
          <label
            onClick={(e) => e.stopPropagation()}
            className="flex items-center cursor-pointer"
            title={compareDisabled ? 'Compare is limited to 3 use cases' : 'Add to comparison'}
          >
            <input
              type="checkbox"
              checked={compareChecked}
              disabled={compareDisabled}
              onChange={onToggleCompare}
              aria-label={`Compare ${brief.title}`}
              className="accent-[#FF6E4C] w-4 h-4 disabled:opacity-40 disabled:cursor-not-allowed"
            />
          </label>
        )}
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#18365E] group-hover:text-[#FF6E4C] transition-colors whitespace-nowrap">
          Open brief
          <ChevronRight className="w-3.5 h-3.5" />
        </span>
      </span>
    </div>
  );
};

export default ExplorerRow;
